import React, { useState } from 'react';
import { 
  Star, 
  Heart, 
  Clock, 
  Search, 
  Copy, 
  Check, 
  GitCompare,
  Filter,
  X,
  ChevronRight,
  Webhook,
  Key,
  Lock,
  Layers,
  Server,
  Zap,
  ArrowRight,
  Network,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useThemeStore } from '../../stores/theme.store';
import { DocumentationCompleteness, FeatureFilter, TemplateViewHistory, TemplateFavorite } from '../../hooks/useProviderTemplateUX';

// Documentation Completeness Bar
interface CompletenessBarProps {
  completeness: DocumentationCompleteness;
  showDetails?: boolean;
}

export function DocumentationCompletenessBar({ completeness, showDetails = false }: CompletenessBarProps) {
  const { isDarkMode } = useThemeStore();
  const [expanded, setExpanded] = useState(false);

  const getColorClass = (percentage: number) => {
    if (percentage >= 80) return isDarkMode ? 'bg-green-500' : 'bg-green-500';
    if (percentage >= 50) return isDarkMode ? 'bg-yellow-500' : 'bg-yellow-500';
    return isDarkMode ? 'bg-red-500' : 'bg-red-500';
  };

  const colors = isDarkMode ? {
    bg: 'bg-gray-700',
    text: 'text-gray-300',
    label: 'text-gray-400',
    sectionBg: 'bg-gray-800',
    sectionBorder: 'border-gray-700',
    filledIcon: 'text-green-400',
    emptyIcon: 'text-gray-600',
  } : {
    bg: 'bg-gray-200',
    text: 'text-gray-700',
    label: 'text-gray-500',
    sectionBg: 'bg-gray-50',
    sectionBorder: 'border-gray-200',
    filledIcon: 'text-green-600',
    emptyIcon: 'text-gray-300',
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className={`text-sm font-medium ${colors.text}`}>
          Documentação: {completeness.percentage}%
        </span>
        {showDetails && (
          <button
            onClick={() => setExpanded(!expanded)}
            className={`text-xs ${colors.label} hover:underline`}
          >
            {expanded ? 'Ocultar detalhes' : 'Ver detalhes'}
          </button>
        )}
      </div>
      
      <div className={`h-2 ${colors.bg} rounded-full overflow-hidden`}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${completeness.percentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className={`h-full ${getColorClass(completeness.percentage)} rounded-full`}
        />
      </div>

      <AnimatePresence>
        {expanded && showDetails && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`${colors.sectionBg} rounded-lg p-3 border ${colors.sectionBorder} mt-2`}
          >
            <div className="grid grid-cols-2 gap-2">
              {completeness.sections.map((section) => (
                <div key={section.name} className="flex items-center gap-2">
                  {section.filled ? (
                    <Check className={`w-4 h-4 ${colors.filledIcon}`} />
                  ) : (
                    <X className={`w-4 h-4 ${colors.emptyIcon}`} />
                  )}
                  <span className={`text-xs ${section.filled ? colors.text : colors.label}`}>
                    {section.name}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Documentation Search Component
interface DocumentationSearchProps {
  onSearch: (term: string) => void;
  matchCount?: number;
  onNavigateMatch?: (direction: 'prev' | 'next') => void;
  currentMatch?: number;
}

export function DocumentationSearch({ onSearch, matchCount = 0, onNavigateMatch, currentMatch = 0 }: DocumentationSearchProps) {
  const { isDarkMode } = useThemeStore();
  const [searchTerm, setSearchTerm] = useState('');

  const colors = isDarkMode ? {
    bg: 'bg-gray-800',
    border: 'border-gray-700',
    text: 'text-white',
    placeholder: 'placeholder-gray-500',
    matchText: 'text-gray-400',
  } : {
    bg: 'bg-white',
    border: 'border-gray-200',
    text: 'text-gray-900',
    placeholder: 'placeholder-gray-400',
    matchText: 'text-gray-500',
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    onSearch(value);
  };

  return (
    <div className={`flex items-center gap-2 p-2 ${colors.bg} rounded-lg border ${colors.border}`}>
      <Search className={`w-4 h-4 ${colors.matchText}`} />
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder="Buscar na documentação..."
        className={`flex-1 bg-transparent ${colors.text} ${colors.placeholder} text-sm outline-none`}
      />
      {matchCount > 0 && (
        <div className="flex items-center gap-1">
          <span className={`text-xs ${colors.matchText}`}>
            {currentMatch + 1}/{matchCount}
          </span>
          {onNavigateMatch && (
            <>
              <button
                onClick={() => onNavigateMatch('prev')}
                className={`p-1 rounded hover:bg-gray-700/50`}
                aria-label="Resultado anterior"
              >
                <ChevronRight className="w-3 h-3 rotate-180" aria-hidden="true" />
              </button>
              <button
                onClick={() => onNavigateMatch('next')}
                className={`p-1 rounded hover:bg-gray-700/50`}
                aria-label="Próximo resultado"
              >
                <ChevronRight className="w-3 h-3" aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      )}
      {searchTerm && (
        <button
          onClick={() => handleSearch('')}
          className={`p-1 rounded hover:bg-gray-700/50`}
          aria-label="Limpar busca"
        >
          <X className="w-3 h-3" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

// Table of Contents Component
interface TableOfContentsProps {
  sections: { id: string; title: string; level: number }[];
  activeSection?: string;
  onNavigate: (sectionId: string) => void;
}

export function TableOfContents({ sections, activeSection, onNavigate }: TableOfContentsProps) {
  const { isDarkMode } = useThemeStore();

  const colors = isDarkMode ? {
    bg: 'bg-gray-800',
    border: 'border-gray-700',
    title: 'text-white',
    item: 'text-gray-400 hover:text-white',
    activeItem: 'text-blue-400 bg-blue-900/30',
  } : {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    title: 'text-gray-900',
    item: 'text-gray-600 hover:text-gray-900',
    activeItem: 'text-blue-600 bg-blue-50',
  };

  if (sections.length === 0) return null;

  return (
    <div className={`${colors.bg} rounded-lg border ${colors.border} p-4`}>
      <h4 className={`text-sm font-semibold ${colors.title} mb-3`}>Índice</h4>
      <nav className="space-y-1">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => onNavigate(section.id)}
            className={`w-full text-left text-sm px-2 py-1 rounded transition-colors ${
              activeSection === section.id ? colors.activeItem : colors.item
            }`}
            style={{ paddingLeft: `${(section.level - 1) * 12 + 8}px` }}
          >
            {section.title}
          </button>
        ))}
      </nav>
    </div>
  );
}

// Favorites Button
interface FavoriteButtonProps {
  isFavorite: boolean;
  onToggle: () => void;
  size?: 'sm' | 'md' | 'lg';
  templateName?: string;
}

export function FavoriteButton({ isFavorite, onToggle, size = 'md', templateName }: FavoriteButtonProps) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const ariaLabel = templateName 
    ? (isFavorite ? `Remover ${templateName} dos favoritos` : `Adicionar ${templateName} aos favoritos`)
    : (isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos');

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={`p-1.5 rounded-lg transition-all ${
        isFavorite 
          ? 'text-red-500 hover:text-red-400 bg-red-500/10' 
          : 'text-gray-400 hover:text-red-500 hover:bg-red-500/10'
      }`}
      title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
      aria-label={ariaLabel}
      aria-pressed={isFavorite}
    >
      <Heart className={`${sizeClasses[size]} ${isFavorite ? 'fill-current' : ''}`} />
    </button>
  );
}

// Recent Templates Panel
interface RecentTemplatesPanelProps {
  history: TemplateViewHistory[];
  onSelect: (templateId: string) => void;
  onClear: () => void;
}

export function RecentTemplatesPanel({ history, onSelect, onClear }: RecentTemplatesPanelProps) {
  const { isDarkMode } = useThemeStore();

  const colors = isDarkMode ? {
    bg: 'bg-gray-800',
    border: 'border-gray-700',
    title: 'text-white',
    item: 'text-gray-300 hover:bg-gray-700',
    time: 'text-gray-500',
    clearBtn: 'text-gray-500 hover:text-gray-300',
  } : {
    bg: 'bg-white',
    border: 'border-gray-200',
    title: 'text-gray-900',
    item: 'text-gray-700 hover:bg-gray-50',
    time: 'text-gray-400',
    clearBtn: 'text-gray-400 hover:text-gray-600',
  };

  if (history.length === 0) return null;

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}min atrás`;
    if (hours < 24) return `${hours}h atrás`;
    return `${days}d atrás`;
  };

  return (
    <div className={`${colors.bg} rounded-lg border ${colors.border} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" />
          <h4 className={`text-sm font-semibold ${colors.title}`}>Visualizados Recentemente</h4>
        </div>
        <button onClick={onClear} className={`text-xs ${colors.clearBtn}`}>
          Limpar
        </button>
      </div>
      <div className="space-y-1">
        {history.map((item) => (
          <button
            key={item.templateId}
            onClick={() => onSelect(item.templateId)}
            className={`w-full flex items-center justify-between p-2 rounded-lg ${colors.item} transition-colors`}
          >
            <span className="text-sm truncate">{item.templateName}</span>
            <span className={`text-xs ${colors.time}`}>{formatTime(item.viewedAt)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Favorites Panel
interface FavoritesPanelProps {
  favorites: TemplateFavorite[];
  onSelect: (templateId: string) => void;
  onRemove: (templateId: string) => void;
}

export function FavoritesPanel({ favorites, onSelect, onRemove }: FavoritesPanelProps) {
  const { isDarkMode } = useThemeStore();

  const colors = isDarkMode ? {
    bg: 'bg-gray-800',
    border: 'border-gray-700',
    title: 'text-white',
    item: 'text-gray-300 hover:bg-gray-700',
    removeBtn: 'text-gray-500 hover:text-red-400',
    emptyText: 'text-gray-500',
  } : {
    bg: 'bg-white',
    border: 'border-gray-200',
    title: 'text-gray-900',
    item: 'text-gray-700 hover:bg-gray-50',
    removeBtn: 'text-gray-400 hover:text-red-500',
    emptyText: 'text-gray-400',
  };

  return (
    <div className={`${colors.bg} rounded-lg border ${colors.border} p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <Heart className="w-4 h-4 text-red-500" />
        <h4 className={`text-sm font-semibold ${colors.title}`}>Favoritos</h4>
        <Badge className="bg-red-500/20 text-red-400 text-xs">{favorites.length}</Badge>
      </div>
      {favorites.length === 0 ? (
        <p className={`text-sm ${colors.emptyText}`}>Nenhum favorito ainda</p>
      ) : (
        <div className="space-y-1">
          {favorites.map((item) => (
            <div
              key={item.templateId}
              className={`flex items-center justify-between p-2 rounded-lg ${colors.item} transition-colors group`}
            >
              <button
                onClick={() => onSelect(item.templateId)}
                className="flex-1 text-left text-sm truncate"
              >
                {item.templateName}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(item.templateId);
                }}
                className={`opacity-0 group-hover:opacity-100 p-1 ${colors.removeBtn} transition-all`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Rating Component
interface RatingComponentProps {
  rating: number;
  totalRatings?: number;
  onRate?: (rating: number) => void;
  readonly?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function RatingComponent({ rating, totalRatings, onRate, readonly = false, size = 'md' }: RatingComponentProps) {
  const [hoverRating, setHoverRating] = useState(0);
  const { isDarkMode } = useThemeStore();

  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const colors = isDarkMode ? {
    filled: 'text-yellow-400',
    empty: 'text-gray-600',
    text: 'text-gray-400',
  } : {
    filled: 'text-yellow-500',
    empty: 'text-gray-300',
    text: 'text-gray-500',
  };

  const displayRating = hoverRating || rating;

  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            disabled={readonly}
            onClick={() => onRate?.(star)}
            onMouseEnter={() => !readonly && setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            className={`${readonly ? 'cursor-default' : 'cursor-pointer'} transition-colors`}
          >
            <Star
              className={`${sizeClasses[size]} ${
                star <= displayRating ? colors.filled : colors.empty
              } ${star <= displayRating ? 'fill-current' : ''}`}
            />
          </button>
        ))}
      </div>
      {totalRatings !== undefined && (
        <span className={`text-xs ${colors.text} ml-1`}>
          ({totalRatings})
        </span>
      )}
    </div>
  );
}

// Feature Filter Panel
interface FeatureFilterPanelProps {
  filters: FeatureFilter;
  onToggle: (key: keyof FeatureFilter) => void;
  onClear: () => void;
  activeCount: number;
}

export function FeatureFilterPanel({ filters, onToggle, onClear, activeCount }: FeatureFilterPanelProps) {
  const { isDarkMode } = useThemeStore();
  const [expanded, setExpanded] = useState(false);

  const colors = isDarkMode ? {
    bg: 'bg-gray-800',
    border: 'border-gray-700',
    title: 'text-white',
    item: 'text-gray-300',
    itemActive: 'bg-blue-900/50 border-blue-500 text-blue-400',
    itemInactive: 'bg-gray-700 border-gray-600 hover:border-gray-500',
  } : {
    bg: 'bg-white',
    border: 'border-gray-200',
    title: 'text-gray-900',
    item: 'text-gray-700',
    itemActive: 'bg-blue-50 border-blue-500 text-blue-600',
    itemInactive: 'bg-gray-50 border-gray-200 hover:border-gray-300',
  };

  const filterOptions = [
    { key: 'hasWebhooks' as const, label: 'Webhooks', icon: Webhook },
    { key: 'hasOAuth' as const, label: 'OAuth 2.0', icon: Lock },
    { key: 'hasApiKey' as const, label: 'API Key', icon: Key },
    { key: 'hasMultipleOperations' as const, label: '3+ Operações', icon: Layers },
  ];

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2"
      >
        <Filter className="w-4 h-4" />
        Recursos
        {activeCount > 0 && (
          <Badge className="bg-blue-500/20 text-blue-400 text-xs">{activeCount}</Badge>
        )}
      </Button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`absolute top-full left-0 mt-2 ${colors.bg} rounded-lg border ${colors.border} p-4 shadow-lg z-20 min-w-[200px]`}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className={`text-sm font-semibold ${colors.title}`}>Filtrar por Recursos</h4>
              {activeCount > 0 && (
                <button onClick={onClear} className="text-xs text-gray-500 hover:text-gray-300">
                  Limpar
                </button>
              )}
            </div>
            <div className="space-y-2">
              {filterOptions.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => onToggle(key)}
                  className={`w-full flex items-center gap-2 p-2 rounded-lg border transition-colors ${
                    filters[key] ? colors.itemActive : colors.itemInactive
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm">{label}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Comparison Button
interface ComparisonButtonProps {
  isInComparison: boolean;
  canAdd: boolean;
  onToggle: () => void;
  templateName?: string;
}

export function ComparisonButton({ isInComparison, canAdd, onToggle, templateName }: ComparisonButtonProps) {
  const ariaLabel = templateName
    ? (isInComparison ? `Remover ${templateName} da comparação` : canAdd ? `Adicionar ${templateName} à comparação` : 'Máximo de 2 templates para comparação')
    : (isInComparison ? 'Remover da comparação' : canAdd ? 'Adicionar à comparação' : 'Máximo de 2 templates');

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      disabled={!isInComparison && !canAdd}
      className={`p-1.5 rounded-lg transition-all ${
        isInComparison
          ? 'text-purple-500 bg-purple-500/10 hover:bg-purple-500/20'
          : canAdd
          ? 'text-gray-400 hover:text-purple-500 hover:bg-purple-500/10'
          : 'text-gray-600 cursor-not-allowed'
      }`}
      title={isInComparison ? 'Remover da comparação' : canAdd ? 'Adicionar à comparação' : 'Máximo de 2 templates'}
      aria-label={ariaLabel}
      aria-pressed={isInComparison}
    >
      <GitCompare className="w-4 h-4" />
    </button>
  );
}

// Copy Config Button
interface CopyConfigButtonProps {
  config: any;
  templateName?: string;
}

export function CopyConfigButton({ config, templateName }: CopyConfigButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(JSON.stringify(config, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const ariaLabel = templateName 
    ? `Copiar configuração do template ${templateName}`
    : 'Copiar configuração do template';

  return (
    <button
      onClick={handleCopy}
      className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700/50 transition-all"
      title="Copiar configuração"
      aria-label={ariaLabel}
    >
      {copied ? (
        <Check className="w-4 h-4 text-green-400" aria-hidden="true" />
      ) : (
        <Copy className="w-4 h-4" aria-hidden="true" />
      )}
      <span className="sr-only">{copied ? 'Copiado!' : 'Copiar'}</span>
    </button>
  );
}

// Integration Diagram Component
interface IntegrationDiagramProps {
  template: any;
}

export function IntegrationDiagram({ template }: IntegrationDiagramProps) {
  const { isDarkMode } = useThemeStore();

  const colors = isDarkMode ? {
    bg: 'bg-gray-800',
    border: 'border-gray-700',
    nodeBg: 'bg-gray-900',
    nodeBorder: 'border-gray-600',
    nodeText: 'text-white',
    line: 'stroke-gray-600',
    arrow: 'text-blue-400',
  } : {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    nodeBg: 'bg-white',
    nodeBorder: 'border-gray-300',
    nodeText: 'text-gray-900',
    line: 'stroke-gray-300',
    arrow: 'text-blue-600',
  };

  return (
    <div className={`${colors.bg} rounded-lg border ${colors.border} p-6`}>
      <h4 className={`text-sm font-semibold ${colors.nodeText} mb-4`}>Diagrama de Integração</h4>
      
      <div className="flex items-center justify-center gap-4">
        {/* Your System */}
        <div className={`${colors.nodeBg} border ${colors.nodeBorder} rounded-lg p-4 text-center`}>
          <Server className={`w-8 h-8 ${colors.arrow} mx-auto mb-2`} />
          <span className={`text-sm ${colors.nodeText}`}>Seu Sistema</span>
        </div>

        {/* Arrow */}
        <div className="flex flex-col items-center">
          <ArrowRight className={`w-6 h-6 ${colors.arrow}`} />
          <span className="text-xs text-gray-500 mt-1">API Request</span>
        </div>

        {/* Provider Engine */}
        <div className={`${colors.nodeBg} border ${colors.nodeBorder} rounded-lg p-4 text-center`}>
          <Zap className={`w-8 h-8 text-yellow-500 mx-auto mb-2`} />
          <span className={`text-sm ${colors.nodeText}`}>Provider Engine</span>
        </div>

        {/* Arrow */}
        <div className="flex flex-col items-center">
          <ArrowRight className={`w-6 h-6 ${colors.arrow}`} />
          <span className="text-xs text-gray-500 mt-1">{template.config?.authConfig?.type || 'Auth'}</span>
        </div>

        {/* External Provider */}
        <div className={`${colors.nodeBg} border ${colors.nodeBorder} rounded-lg p-4 text-center border-2 border-blue-500`}>
          <Network className={`w-8 h-8 text-blue-500 mx-auto mb-2`} />
          <span className={`text-sm ${colors.nodeText}`}>{template.name}</span>
        </div>
      </div>

      {/* Operations */}
      {template.config?.operations && template.config.operations.length > 0 && (
        <div className="mt-6">
          <h5 className={`text-xs font-medium ${colors.nodeText} mb-2`}>Operações Disponíveis:</h5>
          <div className="flex flex-wrap gap-2">
            {template.config.operations.slice(0, 5).map((op: any, idx: number) => (
              <Badge key={idx} className="bg-blue-500/20 text-blue-400 text-xs">
                {op.method} {op.name}
              </Badge>
            ))}
            {template.config.operations.length > 5 && (
              <Badge className="bg-gray-500/20 text-gray-400 text-xs">
                +{template.config.operations.length - 5} mais
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default {
  DocumentationCompletenessBar,
  DocumentationSearch,
  TableOfContents,
  FavoriteButton,
  RecentTemplatesPanel,
  FavoritesPanel,
  RatingComponent,
  FeatureFilterPanel,
  ComparisonButton,
  CopyConfigButton,
  IntegrationDiagram,
};
